import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Transaction, SavingsGoal } from '../../lib/types';
import { cn } from '../../lib/utils';

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingTransaction: Transaction | null;
  defaultType: 'income' | 'expense';
  defaultCategory?: string;
  categories: string[];
  savingsGoals: SavingsGoal[];
  onSave: (data: Omit<Transaction, 'id' | 'uid'>) => Promise<void>;
}

export function TransactionModal({
  isOpen,
  onClose,
  editingTransaction,
  defaultType,
  defaultCategory,
  categories,
  savingsGoals,
  onSave,
}: TransactionModalProps) {
  const [formData, setFormData] = useState({
    type: 'expense' as 'income' | 'expense',
    category: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    note: '',
    allowance: '',
    gift1: '',
    gift2: '',
    relationship: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (editingTransaction) {
        setFormData({
          type: editingTransaction.type,
          category: editingTransaction.category,
          amount: editingTransaction.amount.toString(),
          date: editingTransaction.date,
          note: editingTransaction.note || '',
          allowance: (editingTransaction.allowance || 0).toString(),
          gift1: (editingTransaction.gift1 || 0).toString(),
          gift2: (editingTransaction.gift2 || 0).toString(),
          relationship: editingTransaction.relationship || '',
        });
      } else {
        setFormData({
          type: defaultType,
          category:
            defaultCategory || (defaultType === 'income' ? 'Paycheck' : ''),
          amount: '',
          date: new Date().toISOString().split('T')[0],
          note: '',
          allowance: '',
          gift1: '',
          gift2: '',
          relationship: '',
        });
      }
    }
  }, [isOpen, editingTransaction, defaultType, defaultCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allowance = parseFloat(formData.allowance) || 0;
    const gift1 = parseFloat(formData.gift1) || 0;
    const gift2 = parseFloat(formData.gift2) || 0;

    const amount =
      formData.category === 'Overseas Expenses'
        ? allowance + gift1 + gift2
        : parseFloat(formData.amount);

    await onSave({
      type: formData.type,
      category: formData.category,
      amount,
      date: formData.date,
      note: formData.note,
      allowance,
      gift1,
      gift2,
      relationship: formData.relationship,
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            className="relative w-full max-w-lg overflow-hidden rounded-t-[40px] border border-zinc-100 bg-white shadow-2xl transition-colors sm:rounded-[40px] dark:border-zinc-900 dark:bg-black"
          >
            <div className="space-y-8 p-8">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                  {editingTransaction ? 'Edit' : 'Add'} Transaction
                </h2>
                <button
                  onClick={onClose}
                  className="rounded-full p-2 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  <X className="h-6 w-6 text-zinc-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex rounded-2xl bg-zinc-100 p-1 dark:bg-zinc-950">
                  {(['expense', 'income'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        const incomeCategories = [
                          'Paycheck',
                          'Savings',
                          'Bonus',
                          'Loan',
                          'Others',
                        ];
                        let newCategory = formData.category;
                        if (
                          type === 'income' &&
                          !incomeCategories.includes(formData.category)
                        ) {
                          newCategory = 'Paycheck';
                        } else if (
                          type === 'expense' &&
                          incomeCategories.includes(formData.category)
                        ) {
                          newCategory = '';
                        }
                        setFormData({
                          ...formData,
                          type,
                          category: newCategory,
                        });
                      }}
                      className={cn(
                        'flex-1 rounded-xl py-3 text-xs font-bold tracking-widest uppercase transition-all',
                        formData.type === type
                          ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                          : 'text-zinc-600 dark:text-zinc-500'
                      )}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                      Category
                    </label>
                    {formData.type === 'income' ? (
                      <select
                        required
                        className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                      >
                        <option value="" disabled>
                          Select Category
                        </option>
                        <option value="Paycheck">Paycheck</option>
                        <option value="Savings">Savings</option>
                        <option value="Bonus">Bonus</option>
                        <option value="Loan">Loan</option>
                        <option value="Others">Others</option>
                      </select>
                    ) : defaultCategory === 'Overseas Expenses' ? (
                      <select
                        required
                        className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                      >
                        <option value="Overseas Expenses">
                          Overseas Expenses
                        </option>
                      </select>
                    ) : (
                      <select
                        required
                        className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                      >
                        <option value="" disabled>
                          Select Category
                        </option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {formData.category === 'Overseas Expenses' ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                          <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                            Allowance
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                            value={formData.allowance}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                allowance: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                            Gift 1
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                            value={formData.gift1}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                gift1: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                            Gift 2
                          </label>
                          <input
                            type="number"
                            placeholder="0"
                            className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                            value={formData.gift2}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                gift2: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                          Relationship
                        </label>
                        <select
                          required
                          className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                          value={formData.relationship}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              relationship: e.target.value,
                            })
                          }
                        >
                          <option value="" disabled>
                            Select Relationship
                          </option>
                          <option value="RELATIVE">Relative</option>
                          <option value="FRIEND">Friend</option>
                          <option value="ACQUAINTANCE">Acquaintance</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-1.5">
                        <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                          Amount
                        </label>
                        <input
                          required
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                          value={formData.amount}
                          onChange={(e) =>
                            setFormData({ ...formData, amount: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                      Date
                    </label>
                    <input
                      required
                      type="date"
                      className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                      {formData.category === 'Savings'
                        ? 'Select Savings Goal'
                        : 'Note (Optional)'}
                    </label>
                    {formData.category === 'Savings' ? (
                      <select
                        required
                        className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                        value={formData.note}
                        onChange={(e) =>
                          setFormData({ ...formData, note: e.target.value })
                        }
                      >
                        <option value="" disabled>
                          Select a Goal
                        </option>
                        {savingsGoals.map((goal) => (
                          <option key={goal.id} value={goal.name}>
                            {goal.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="Add a description..."
                        className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                        value={formData.note}
                        onChange={(e) =>
                          setFormData({ ...formData, note: e.target.value })
                        }
                      />
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full rounded-[24px] bg-zinc-900 py-5 font-bold text-white shadow-sm shadow-zinc-200 transition-transform active:scale-95 dark:bg-zinc-50 dark:text-zinc-900"
                >
                  Save Transaction
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
